import db from './db';
import {
  getFullState,
  saveFullState,
  onStateChanged,
  parseStateBlob,
  toWirePattern,
} from './persistence';
import { getSettings, saveSettings, subscribeSettings } from './settings';
import { createBackend } from './backends';
import { RemoteNotFoundError } from './backends/types';
import type { BackendConfig, RemoteBackend } from './backends/types';
import { encodeSession } from './eventCodec';
import { CURRENT_STATE_VERSION } from '../types/state';
import type { KnittingState } from '../types/state';
import type { AppSettings } from '../types/settings';

export type SyncStatus = 'idle' | 'syncing' | 'synced' | 'conflict' | 'error' | 'offline';

export type RemoteFiles = Record<string, string | undefined>;

export interface SyncResult {
  status: SyncStatus;
  message?: string;
}

// ─── Sync status store ────────────────────────────────────────────────────

export interface SyncState {
  status: SyncStatus;
  message?: string;
  lastResultAt?: string;
}

let syncState: SyncState = { status: 'idle' };
type SyncCallback = (s: SyncState) => void;
const syncCallbacks: SyncCallback[] = [];

export function getSyncState(): SyncState {
  return syncState;
}

export function subscribeSyncStatus(cb: SyncCallback): () => void {
  syncCallbacks.push(cb);
  return () => {
    const idx = syncCallbacks.indexOf(cb);
    if (idx >= 0) syncCallbacks.splice(idx, 1);
  };
}

function setSyncState(next: SyncState): void {
  syncState = next;
  syncCallbacks.forEach((cb) => cb(next));
}

function emitResult(result: SyncResult): SyncResult {
  setSyncState({
    status: result.status,
    message: result.message,
    lastResultAt: new Date().toISOString(),
  });
  return result;
}

// ─── Pending-conflict store ──────────────────────────────────────────────

export interface PendingConflict {
  remoteFiles: RemoteFiles;
  remoteUpdatedAt: string;
  remoteCursor?: string;
}

let pendingConflict: PendingConflict | null = null;
type ConflictCallback = (c: PendingConflict | null) => void;
const conflictCallbacks: ConflictCallback[] = [];

export function getPendingConflict(): PendingConflict | null {
  return pendingConflict;
}

export function subscribeConflict(cb: ConflictCallback): () => void {
  conflictCallbacks.push(cb);
  return () => {
    const idx = conflictCallbacks.indexOf(cb);
    if (idx >= 0) conflictCallbacks.splice(idx, 1);
  };
}

export function setPendingConflict(c: PendingConflict | null): void {
  pendingConflict = c;
  conflictCallbacks.forEach((cb) => cb(c));
}

// ─────────────────────────────────────────────────────────────────────────────

const PATTERNS_FILE = 'patterns.json';
const SESSIONS_FILE = 'sessions.json';

// Storage key kept the same for forward-compatibility with older clients.
// The semantics are: "hashes of patterns.json/sessions.json as of the last
// successful push or pull." When current local hashes match these, local
// is clean — i.e. it agrees byte-for-byte with the remote at our last sync.
const CLEAN_HASHES_KEY = 'last-sync-hashes';

interface CleanHashes {
  patterns?: string;
  sessions?: string;
}

async function getCleanHashes(): Promise<CleanHashes> {
  return (await db.getItem<CleanHashes>(CLEAN_HASHES_KEY)) ?? {};
}

async function setCleanHashes(h: CleanHashes): Promise<void> {
  await db.setItem(CLEAN_HASHES_KEY, h);
  invalidateDirtyCache();
}

async function sha256(s: string): Promise<string> {
  const buf = new TextEncoder().encode(s);
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function hashSerialized(serialized: {
  patterns: string;
  sessions: string;
}): Promise<CleanHashes> {
  const [patterns, sessions] = await Promise.all([
    sha256(serialized.patterns),
    sha256(serialized.sessions),
  ]);
  return { patterns, sessions };
}

async function hashSnapshot(files: RemoteFiles): Promise<CleanHashes> {
  const [patterns, sessions] = await Promise.all([
    files[PATTERNS_FILE] ? sha256(files[PATTERNS_FILE]!) : Promise.resolve(undefined),
    files[SESSIONS_FILE] ? sha256(files[SESSIONS_FILE]!) : Promise.resolve(undefined),
  ]);
  return { patterns, sessions };
}

function hashesEqual(a: CleanHashes, b: CleanHashes): boolean {
  return a.patterns === b.patterns && a.sessions === b.sessions;
}

// ─── Backend resolution ───────────────────────────────────────────────────

interface ResolvedBackend {
  settings: AppSettings;
  config: BackendConfig;
  backend: RemoteBackend;
}

/**
 * Load settings and instantiate the configured backend, or return a sync
 * result describing why we can't sync (no config, offline, etc.).
 */
async function resolveBackend(): Promise<ResolvedBackend | SyncResult> {
  const settings = await getSettings();
  if (!settings.backend)
    return emitResult({ status: 'error', message: 'No sync backend configured' });
  if (!navigator.onLine) return emitResult({ status: 'offline', message: 'Device is offline' });
  return {
    settings,
    config: settings.backend,
    backend: createBackend(settings.backend),
  };
}

async function updateBackendConfig(patch: Partial<BackendConfig>): Promise<void> {
  const settings = await getSettings();
  if (!settings.backend) return;
  await saveSettings({
    ...settings,
    backend: { ...settings.backend, ...patch } as BackendConfig,
  });
}

/**
 * Drop per-instance state (remoteId/cursor/lastSyncedAt) when the upstream
 * record is gone, so the next push creates fresh. Auth is preserved.
 */
async function forgetRemote(): Promise<void> {
  await updateBackendConfig({ remoteId: undefined, cursor: undefined, lastSyncedAt: undefined });
  await setCleanHashes({});
}

// ─── Serialization helpers ────────────────────────────────────────────────

// Build the two split remote files from in-memory state. Patterns and
// sessions get the same per-item wire conversion (and version stamping) as
// the local persistence layer; `unknownPatterns` / `unknownSessions` flow
// through unchanged so a client that can't parse them still preserves them
// for the next client.
export function serializeFiles(state: KnittingState): { patterns: string; sessions: string } {
  const wirePatterns: Record<string, unknown> = {};
  for (const [id, p] of Object.entries(state.patterns)) wirePatterns[id] = toWirePattern(p);
  for (const [id, blob] of Object.entries(state.unknownPatterns ?? {})) wirePatterns[id] = blob;

  const wireSessions: Record<string, unknown> = {};
  for (const [id, s] of Object.entries(state.sessions)) wireSessions[id] = encodeSession(s);
  for (const [id, blob] of Object.entries(state.unknownSessions ?? {})) wireSessions[id] = blob;

  return {
    patterns: JSON.stringify({ version: CURRENT_STATE_VERSION, patterns: wirePatterns }),
    sessions: JSON.stringify({ version: CURRENT_STATE_VERSION, sessions: wireSessions }),
  };
}

// Recombine the two split remote files into one state-shaped envelope and
// run it through the same per-item parse path as local persistence — so
// per-item validation failures become opaque blobs preserved on round-trip,
// rather than tearing down the whole pull.
async function parseFiles(files: RemoteFiles): Promise<KnittingState> {
  const patternsRaw = files[PATTERNS_FILE] ? JSON.parse(files[PATTERNS_FILE]) : {};
  const sessionsRaw = files[SESSIONS_FILE] ? JSON.parse(files[SESSIONS_FILE]) : {};
  const combined = {
    version: patternsRaw.version ?? sessionsRaw.version,
    patterns: patternsRaw.patterns ?? {},
    sessions: sessionsRaw.sessions ?? {},
  };
  const { version, patterns, sessions, unknownPatterns, unknownSessions } =
    await parseStateBlob(combined);
  return { version, patterns, sessions, unknownPatterns, unknownSessions };
}

// ─── Dirty-state observable ───────────────────────────────────────────────
//
// `isDirty` is derived: hash current local state, compare to recorded
// `cleanHashes`. We cache the boolean and recompute lazily so consumers can
// subscribe and only re-render on actual transitions. Each invalidation
// bumps a version counter; only the latest-versioned compute writes to the
// cache, so a state change that races with an in-flight hash-and-compare
// can't pin a stale result.

let dirtyCache: boolean | null = null;
let dirtyComputeVersion = 0;
let lastNotifiedDirty: boolean | null = null;
type DirtyCallback = (isDirty: boolean) => void;
const dirtyCallbacks: DirtyCallback[] = [];

function maybeNotifyDirty(next: boolean): void {
  if (lastNotifiedDirty === next) return;
  lastNotifiedDirty = next;
  dirtyCallbacks.forEach((cb) => cb(next));
}

function invalidateDirtyCache(): void {
  dirtyCache = null;
  const myVersion = ++dirtyComputeVersion;
  // Eagerly recompute so subscribers can be notified on transitions even
  // if no consumer awaits getIsDirty.
  void (async () => {
    const next = await computeDirty();
    if (myVersion !== dirtyComputeVersion) return;
    dirtyCache = next;
    maybeNotifyDirty(next);
  })();
}

async function computeDirty(): Promise<boolean> {
  const [state, clean] = await Promise.all([getFullState(), getCleanHashes()]);
  // Until we've synced once, "empty local" should not show as dirty —
  // there's nothing to push. Any actual content (parsed or quarantined as
  // unknownPatterns/Sessions) counts.
  if (!clean.patterns && !clean.sessions) {
    const total =
      Object.keys(state.patterns).length +
      Object.keys(state.unknownPatterns ?? {}).length +
      Object.keys(state.sessions).length +
      Object.keys(state.unknownSessions ?? {}).length;
    return total > 0;
  }
  const localHashes = await hashSerialized(serializeFiles(state));
  return !hashesEqual(localHashes, clean);
}

export async function getIsDirty(): Promise<boolean> {
  if (dirtyCache !== null) return dirtyCache;
  const myVersion = ++dirtyComputeVersion;
  const next = await computeDirty();
  if (myVersion === dirtyComputeVersion) {
    dirtyCache = next;
    maybeNotifyDirty(next);
  }
  return next;
}

export function subscribeDirty(cb: DirtyCallback): () => void {
  dirtyCallbacks.push(cb);
  return () => {
    const idx = dirtyCallbacks.indexOf(cb);
    if (idx >= 0) dirtyCallbacks.splice(idx, 1);
  };
}

// State changes might flip dirty. Wire this once; re-entrancy is harmless
// because the unsubscribe is idempotent if we ever need it.
let dirtyWatcherInstalled = false;
function ensureDirtyWatcher(): void {
  if (dirtyWatcherInstalled) return;
  dirtyWatcherInstalled = true;
  onStateChanged(invalidateDirtyCache);
  subscribeSettings(invalidateDirtyCache);
}
ensureDirtyWatcher();

// ─── Push / Pull ──────────────────────────────────────────────────────────

export async function pushToRemote(): Promise<SyncResult> {
  setSyncState({ status: 'syncing' });
  const resolved = await resolveBackend();
  if ('status' in resolved) return resolved;
  const { config, backend } = resolved;

  try {
    const state = await getFullState();
    const serialized = serializeFiles(state);
    const filePayload = {
      [PATTERNS_FILE]: serialized.patterns,
      [SESSIONS_FILE]: serialized.sessions,
    };
    const localHashes = await hashSerialized(serialized);

    if (!config.remoteId) {
      const result = await backend.create(filePayload);
      await setCleanHashes(localHashes);
      await updateBackendConfig({
        remoteId: result.remoteId,
        cursor: result.cursor,
        lastSyncedAt: result.updatedAt,
      });
      return emitResult({ status: 'synced', message: 'Created new remote' });
    }

    const cleanHashes = await getCleanHashes();
    const partial: Record<string, string> = {};
    if (cleanHashes.patterns !== localHashes.patterns) partial[PATTERNS_FILE] = serialized.patterns;
    if (cleanHashes.sessions !== localHashes.sessions) partial[SESSIONS_FILE] = serialized.sessions;

    if (Object.keys(partial).length === 0) {
      return emitResult({ status: 'synced', message: 'Already up to date' });
    }

    try {
      const result = await backend.update(config.remoteId, partial);
      await setCleanHashes(localHashes);
      await updateBackendConfig({ cursor: result.cursor, lastSyncedAt: result.updatedAt });
      return emitResult({ status: 'synced' });
    } catch (err) {
      if (err instanceof RemoteNotFoundError) {
        // Remote was deleted upstream — drop the stale id and create a fresh one.
        await forgetRemote();
        const result = await backend.create(filePayload);
        await setCleanHashes(localHashes);
        await updateBackendConfig({
          remoteId: result.remoteId,
          cursor: result.cursor,
          lastSyncedAt: result.updatedAt,
        });
        return emitResult({ status: 'synced', message: 'Remote was missing — created a new one' });
      }
      throw err;
    }
  } catch (err) {
    return emitResult({ status: 'error', message: String(err) });
  }
}

/**
 * Pull remote into local state. Pull is unconditionally safe when local is
 * clean (we put our state on remote, so accepting whatever is there cannot
 * lose work). When local is dirty, we only conflict if remote has also moved
 * since our last sync.
 */
export async function pullFromRemote(force = false): Promise<SyncResult> {
  setSyncState({ status: 'syncing' });
  const resolved = await resolveBackend();
  if ('status' in resolved) return resolved;
  const { config, backend } = resolved;
  if (!config.remoteId) return emitResult({ status: 'error', message: 'Remote not configured' });

  try {
    let snapshot;
    try {
      snapshot = await backend.fetch(config.remoteId, config.cursor);
    } catch (err) {
      if (err instanceof RemoteNotFoundError) {
        await forgetRemote();
        return emitResult({
          status: 'error',
          message: 'Remote no longer exists. Cleared local link — push to create a new one.',
        });
      }
      throw err;
    }
    if (!snapshot) return emitResult({ status: 'synced', message: 'Already up to date' });

    if (!force) {
      const dirty = await getIsDirty();
      if (dirty) {
        const cleanHashes = await getCleanHashes();
        const remoteHashes = await hashSnapshot(snapshot.files);
        if (hashesEqual(remoteHashes, cleanHashes)) {
          // Remote is at our last-synced state; we have unpushed local
          // changes. Nothing to pull. Refresh cursor and return synced.
          await updateBackendConfig({
            cursor: snapshot.cursor,
            lastSyncedAt: snapshot.updatedAt,
          });
          return emitResult({ status: 'synced', message: 'Already up to date' });
        }
        setPendingConflict({
          remoteFiles: snapshot.files,
          remoteUpdatedAt: snapshot.updatedAt,
          remoteCursor: snapshot.cursor,
        });
        return emitResult({ status: 'conflict' });
      }
    }

    // Local is clean (or force=true): apply remote unconditionally.
    const remoteState = await parseFiles(snapshot.files);
    await saveFullState(remoteState);
    const remoteHashes = await hashSnapshot(snapshot.files);
    await setCleanHashes(remoteHashes);
    await updateBackendConfig({ cursor: snapshot.cursor, lastSyncedAt: snapshot.updatedAt });
    return emitResult({ status: 'synced' });
  } catch (err) {
    return emitResult({ status: 'error', message: String(err) });
  }
}

/**
 * Apply a remote payload (e.g. when resolving a conflict in favour of remote).
 * Records the new clean state and, if cursor/updatedAt are supplied, refreshes
 * backend bookkeeping in one shot.
 */
export async function applyRemoteFiles(
  files: RemoteFiles,
  meta?: { cursor?: string; updatedAt?: string },
): Promise<void> {
  const remoteState = await parseFiles(files);
  await saveFullState(remoteState);
  const remoteHashes = await hashSnapshot(files);
  await setCleanHashes(remoteHashes);
  if (meta?.cursor !== undefined || meta?.updatedAt !== undefined) {
    await updateBackendConfig({
      ...(meta.cursor !== undefined ? { cursor: meta.cursor } : {}),
      ...(meta.updatedAt !== undefined ? { lastSyncedAt: meta.updatedAt } : {}),
    });
  }
}

/**
 * Bidirectional sync. The decision matrix:
 *
 * | local clean | remote at clean | action               |
 * |-------------|-----------------|----------------------|
 * | yes         | yes             | refresh cursor; noop |
 * | yes         | no              | pull (auto-apply)    |
 * | no          | yes             | push                 |
 * | no          | no              | conflict             |
 *
 * "Remote at clean" means the remote's bytes match our recorded clean hashes
 * — either via a 304 from the cursor, or by hashing the snapshot.
 */
export async function syncBidirectional(): Promise<SyncResult> {
  setSyncState({ status: 'syncing' });
  const resolved = await resolveBackend();
  if ('status' in resolved) return resolved;
  const { config, backend } = resolved;
  if (!config.remoteId) return pushToRemote();

  try {
    let snapshot;
    try {
      snapshot = await backend.fetch(config.remoteId, config.cursor);
    } catch (err) {
      if (err instanceof RemoteNotFoundError) {
        // Remote was deleted upstream — recover by recreating from our local copy.
        await forgetRemote();
        return pushToRemote();
      }
      throw err;
    }

    const localDirty = await getIsDirty();

    if (!snapshot) {
      // 304 — remote unchanged since cursor. Push if we have local changes.
      if (localDirty) return pushToRemote();
      return emitResult({ status: 'synced', message: 'Already up to date' });
    }

    const cleanHashes = await getCleanHashes();
    const noClean = !cleanHashes.patterns && !cleanHashes.sessions;
    if (noClean) {
      // First sync against an existing remote (e.g. after QR handoff).
      // Treat the remote as the source of truth and pull.
      const remoteState = await parseFiles(snapshot.files);
      await saveFullState(remoteState);
      const remoteHashes = await hashSnapshot(snapshot.files);
      await setCleanHashes(remoteHashes);
      await updateBackendConfig({
        cursor: snapshot.cursor,
        lastSyncedAt: snapshot.updatedAt,
      });
      return emitResult({ status: 'synced', message: 'Pulled from existing remote' });
    }

    const remoteHashes = await hashSnapshot(snapshot.files);
    const remoteAtClean = hashesEqual(remoteHashes, cleanHashes);

    if (!localDirty && remoteAtClean) {
      // Both sides agree. Refresh cursor in case the server's etag/updatedAt
      // moved (e.g. clock drift) without any actual content change.
      await updateBackendConfig({
        cursor: snapshot.cursor,
        lastSyncedAt: snapshot.updatedAt,
      });
      return emitResult({ status: 'synced', message: 'Already up to date' });
    }

    if (!localDirty && !remoteAtClean) {
      // Pull is unconditionally safe: local is clean, so we'd just be
      // overwriting our own last-pushed bytes with whatever's there.
      const remoteState = await parseFiles(snapshot.files);
      await saveFullState(remoteState);
      await setCleanHashes(remoteHashes);
      await updateBackendConfig({
        cursor: snapshot.cursor,
        lastSyncedAt: snapshot.updatedAt,
      });
      return emitResult({ status: 'synced', message: 'Pulled remote changes' });
    }

    if (localDirty && remoteAtClean) {
      // We're ahead of remote. Push.
      return pushToRemote();
    }

    // Both moved. Real conflict.
    setPendingConflict({
      remoteFiles: snapshot.files,
      remoteUpdatedAt: snapshot.updatedAt,
      remoteCursor: snapshot.cursor,
    });
    return emitResult({ status: 'conflict' });
  } catch (err) {
    return emitResult({ status: 'error', message: String(err) });
  }
}

// ─── Debounced auto-sync ───────────────────────────────────────────────────

let syncTimer: ReturnType<typeof setTimeout> | null = null;
const SYNC_DEBOUNCE_MS = 30_000;

function scheduleAutoSync(): void {
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(async () => {
    syncTimer = null;
    const settings = await getSettings();
    if (!settings.autoSync || !settings.backend) return;
    await syncBidirectional();
  }, SYNC_DEBOUNCE_MS);
}

let unsubscribe: (() => void) | null = null;

export function initAutoSync(): () => void {
  if (unsubscribe) unsubscribe();
  unsubscribe = onStateChanged(scheduleAutoSync);
  return () => {
    unsubscribe?.();
    unsubscribe = null;
    if (syncTimer) {
      clearTimeout(syncTimer);
      syncTimer = null;
    }
  };
}

// Test-only escape hatch. Lets E2E inject a synthetic conflict without
// standing up a real Gist backend. Gated on `import.meta.env.DEV` so it
// is a no-op in production builds.
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (
    window as unknown as { __setPendingConflictForTest: typeof setPendingConflict }
  ).__setPendingConflictForTest = setPendingConflict;
}
